import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import {
  type User,
} from 'firebase/auth'
import { auth, onAuthChange, channel, firebaseLogout, clearFirebaseStorage } from '@app/firebase'
import { authByMagicLink, sendMagicLinkEmail } from '@app/helpers/auth'

interface FirebaseContextValue {
  user: User | null
  isReady: boolean
  sendMagicLink: (email: string) => Promise<void>
  handleMagicLinkSignIn: () => Promise<{e?: any, user?: any, idToken?: string}>
  logout: () => Promise<void>
  clearStorage: () => Promise<void>
}

const FirebaseContext = createContext<FirebaseContextValue>({
  user: null,
  isReady: false,
  sendMagicLink: async () => null,
  handleMagicLinkSignIn: async () => null,
  logout: async () => null,
  clearStorage: async () => null,
})

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isReady, setReady] = useState(false)

  useEffect(() => {
    if(!auth) return
    const unsub = onAuthChange((u) => {
      if(u) {
        setUser(u)
        setReady(true)
      }
    })
    const oobCode = (new URLSearchParams(window.location.href)).get('oobCode')
    if(oobCode) localStorage.setItem('auth_type', 'magic_link')
    return unsub
  }, [])

  const handleMagicLinkSignIn = useCallback(authByMagicLink, [])

  const sendMagicLink = useCallback(sendMagicLinkEmail, [])

  const logout = useCallback(firebaseLogout, [])

  const clearStorage = useCallback(clearFirebaseStorage, [])

  return (
    <FirebaseContext.Provider
      value={{
        user,
        isReady,
        sendMagicLink,
        handleMagicLinkSignIn,
        logout,
        clearStorage
      }}
    >
      {children}
    </FirebaseContext.Provider>
  )
}

export const useFirebase = () => useContext(FirebaseContext)
